import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Screen } from "@/components/screen";
import { Button } from "@/components/ui";
import { useToast } from "@/contexts";
import { getFriends } from "@/features/friends/api";
import type { FriendProfile } from "@/types";

export default function Friends() {
  const navigate = useNavigate();
  const [friends, setFriends] = useState<FriendProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const loadData = useCallback(async () => {
    setLoading(true);
    const { data, error } = await getFriends();
    if (error) toast.showAlert("Error", error.message, "error");
    else setFriends(data ?? []);
    setLoading(false);
  }, [toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <Screen eyebrow="Social" title="Friends" subtitle="Manage your friends">
      <Button label="Add friend" kind="primary" onPress={() => navigate({ to: "/friends/add" })} />

      {loading && <span className="text-text-muted text-sm text-center mt-3">Loading...</span>}

      {!loading && (
        <div className="bg-card rounded-[20px] p-4 border border-border shadow-[1px_1px_0px_0px_#1c1b2e] flex flex-col gap-1.5">
          {friends.length === 0 && (
            <div className="flex flex-col items-center py-4 gap-1.5">
              <span className="text-2xl" role="img" aria-label="Friends">
                👥
              </span>
              <span className="text-text-dim text-sm italic text-center">
                No friends yet. Add some to get started!
              </span>
            </div>
          )}
          {friends.map((friend) => (
            <div
              key={friend.id}
              className="flex flex-row items-center gap-3 py-2 border-b border-border last:border-0"
            >
              <div className="w-10 h-10 rounded-full bg-[rgba(255,45,126,0.1)] flex items-center justify-center overflow-hidden shrink-0">
                {friend.avatarUrl ? (
                  <img
                    src={friend.avatarUrl}
                    alt=""
                    className="w-10 h-10"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <span className="text-aqua text-base font-extrabold">
                    {friend.displayName.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1 flex flex-col gap-0.5">
                <span className="text-text text-sm font-bold">{friend.displayName}</span>
                <span className="text-text-muted text-xs">@{friend.username}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Screen>
  );
}
